import { zeroPad } from 'react-countdown';
import { useTranslation } from 'react-i18next';

import { IconWithText } from '@/components/ui/custom/dataDisplay/IconWithText';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfoSideModal from '@/components/ui/custom/InfoSideModal';
import { LessonBlockProgressCollection } from '@/components/dashboard/student/lessonProgressCollection/LessonProgressCollection';
import useToggle from '@/data/hook/useToggle';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { preventDefault } from '@/utils/mouseEvent';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

export type Props = {
  totalLessons: number;
  remainingLessons: number;
  courseProgress: SimplifiedCourseProgress;
  className?: Pick<ClassNameComponent, 'text' | 'base' | 'section'>;
};

export default function CoursePathProgressStats(props: Props) {
  const { totalLessons, remainingLessons, courseProgress, className } = props;

  const courseName = courseProgress.coursePath.course.name;
  const courseProgressId = courseProgress.id;
  const slugCourseName = courseProgress.coursePath.slug;
  const enrollmentStatus = courseProgress.enrollmentStatus;
  const klassId = courseProgress.klassId;

  const [t] = useTranslation('translation', {
    keyPrefix: 'courseCard.progressStats',
  });

  const { isOpen, open, close } = useToggle();

  const onClick: MouseEventHandler<HTMLButtonElement> = e => {
    preventDefault(e);
    open();
  };

  const modalTrigger = (
    <button
      onClick={onClick}
      className="hover:opacity-80 transition-opacity cursor-pointer"
    >
      <IconWithText
        className={{
          base: twMerge(
            'flex-row-reverse gap-1 self-center',
            className?.section,
          ),
          text: twMerge('text-14 font-500', className?.text),
        }}
        icon={<BookOpenIcon className="w-4 h-4 mb-0.5" />}
        text={t('totalLessons', { count: totalLessons })}
      />
    </button>
  );

  return (
    <span
      className={twMerge(
        'text-[1rem] flex justify-between items-center w-full',
        className?.base,
      )}
      onClick={preventDefault}
    >
      <Text
        text={t('remainingLessons', {
          remaining: zeroPad(remainingLessons, 2),
        })}
        className={twMerge('text-14', className?.text)}
      />
      <InfoSideModal
        trigger={modalTrigger}
        subtitle={t('totalLessons', {
          count: totalLessons,
        })}
        open={isOpen}
        onClose={close}
        title={courseName}
        className={{
          base: twMerge('overflow-y-auto', scrollBarYClassName),
        }}
      >
        <section className={twMerge('flex flex-col gap-2.5 p-2')}>
          <LessonBlockProgressCollection
            courseProgressId={courseProgressId}
            slugCourseName={slugCourseName}
            enrollmentStatus={enrollmentStatus}
            klassId={klassId}
          />
        </section>
      </InfoSideModal>
    </span>
  );
}
